import DOMPurify from "dompurify";

/**
 * NOTE: client-only. `dompurify` needs a DOM (`window`/`document`) and this is a
 * client-rendered Vite SPA. If SSR/edge rendering is ever introduced this import
 * must be guarded (e.g. `createDOMPurify(window)` only in the browser).
 *
 * Guards against stored XSS from author-supplied HTML (employer job/organization
 * details, community event descriptions): those values come back from the API
 * verbatim, so a malicious author could otherwise inject `<script>` /
 * `<img onerror=...>` that runs in another user's session.
 */

/**
 * Inert formatting tags the Tiptap editor (StarterKit + Underline + Link +
 * lists) can emit. Anything else — `<script>`, `<iframe>`, `<style>`,
 * `<object>`, `<form>`, raw `<div>`, inline event handlers — is stripped. The
 * base set called out in review is `a, strong, em, ul, ol, li, p, br, span`;
 * the rest are the remaining safe formatting tags the editor produces.
 */
const ALLOWED_TAGS = [
  "p",
  "br",
  "span",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "del",
  "sub",
  "sup",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "ul",
  "ol",
  "li",
  "blockquote",
  "code",
  "pre",
  "hr",
  "a",
];

const ALLOWED_ATTR = ["href", "target", "rel", "class", "title"];

/**
 * `class` is kept because some render sites have no `prose`/parent-selector
 * fallback and rely on the editor's emitted classes for list/link styling — but
 * the values are restricted to exactly the tokens the Tiptap config emits, so
 * author HTML can't smuggle in layout/positioning utilities (e.g.
 * `fixed inset-0 z-50`) for CSS-based UI spoofing. Keep in sync with the
 * `HTMLAttributes` in `src/components/Tiptap`.
 */
const ALLOWED_CLASSES = new Set([
  "list-disc",
  "list-decimal",
  "px-6",
  "my-3",
  "text-primary",
  "underline",
  "underline-offset-2",
  "cursor-pointer",
  "mention",
  "font-semibold",
]);

// `addHook` appends and does not dedupe; clearing first keeps exactly one hook
// even if this module initializer re-runs (Jest module reset / Vite HMR).
DOMPurify.removeHooks("afterSanitizeAttributes");
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  // Restrict class to the known-safe allowlist (prevents CSS-based UI spoofing).
  if (node.hasAttribute("class")) {
    const safe = (node.getAttribute("class") ?? "")
      .split(/\s+/)
      .filter((token) => ALLOWED_CLASSES.has(token));
    if (safe.length > 0) node.setAttribute("class", safe.join(" "));
    else node.removeAttribute("class");
  }

  // Links: only `_blank` may open a new browsing context (and it gets
  // reverse-tabnabbing protection); any other `target` (`_top`/`_parent`/…) is
  // dropped so author HTML can't navigate ancestor frames if ever embedded.
  if (node.tagName === "A") {
    const target = node.getAttribute("target");
    if (target && target !== "_blank") node.removeAttribute("target");
    if (node.getAttribute("target") === "_blank") {
      node.setAttribute("rel", "noopener noreferrer");
    }
  }
});

/**
 * Sanitize untrusted HTML before rendering it via `dangerouslySetInnerHTML`.
 * Returns an empty string for nullish input so callers can pass optional API
 * fields directly.
 */
export const sanitizeHtml = (dirty?: string | null): string =>
  dirty ? DOMPurify.sanitize(dirty, { ALLOWED_TAGS, ALLOWED_ATTR }) : "";
